import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, HomeIcon, MapIcon } from '@heroicons/react/24/outline';
import PremiumPageLayout, { PremiumPageContent } from '../components/layouts/PremiumPageLayout';
import Button from '../components/general/Button';

/**
 * Page d'erreur 404 globale, branchée sur `path: '*'` dans le router.
 * Visuellement alignée avec PremiumPageLayout (gradient + texture) pour
 * que l'utilisateur reste dans le langage du produit même quand il
 * tombe sur une route inconnue. Affiche le path tenté pour aider au
 * debug et propose un retour.
 */
export default function NotFound() {
    const { t } = useTranslation('common');
    const location = useLocation();
    const navigate = useNavigate();

    const attemptedPath = `${location.pathname}${location.search}`;

    return (
        <PremiumPageLayout>
            <PremiumPageContent>
                <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
                    <div className="w-full max-w-xl bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
                        <div className="flex flex-col items-center text-center p-10">
                            {/* Visuel : "404" en filigrane + icône */}
                            <div className="relative mb-6">
                                <p
                                    className="font-bold text-gray-100 leading-none select-none"
                                    style={{ fontSize: '8rem' }}
                                    aria-hidden="true"
                                >
                                    404
                                </p>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-20 h-20 rounded-2xl bg-white border border-gray-200/80 shadow-md flex items-center justify-center">
                                        <MapIcon className="w-10 h-10 text-main-color" />
                                    </div>
                                </div>
                            </div>

                            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                                {t('not_found.headline')}
                            </h1>
                            <p className="text-sm text-gray-500 max-w-md mb-6 leading-relaxed">
                                {t('not_found.description')}
                            </p>

                            {/* Path tenté pour debug — pas un secret, juste la barre d'URL */}
                            <div className="w-full bg-gray-50 border border-gray-200/80 rounded-lg px-4 py-3 mb-8">
                                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                                    {t('not_found.attempted_path')}
                                </p>
                                <p className="text-sm font-mono text-gray-700 break-all">
                                    {attemptedPath}
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                <Button
                                    type="default"
                                    elementType="button"
                                    attributes={{
                                        onClick: () => navigate(-1),
                                        className: 'justify-center',
                                    }}
                                >
                                    <ArrowLeftIcon className="w-4 h-4 mr-2" />
                                    {t('not_found.go_back')}
                                </Button>
                                <Button
                                    type="primary"
                                    elementType="link"
                                    attributes={{
                                        to: '/',
                                        className: 'justify-center',
                                    }}
                                >
                                    <HomeIcon className="w-4 h-4 mr-2" />
                                    {t('not_found.back_home')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </PremiumPageContent>
        </PremiumPageLayout>
    );
}
